import type { ApiError, Mode } from '@oasis/contracts';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

/**
 * Configuration d'exécution, servie par le conteneur web (`/config.json`, générée
 * au déploiement) : le même artefact sert la préproduction et la production (R24).
 */
export interface RuntimeConfig {
  mode: Mode;
  identityUrl: string;
  realm: string;
  clientId: string;
  /** Version déployée (écrite au déploiement) : une page plus ancienne se met à jour. */
  version?: string;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) throw new Error('Configuration indisponible');
  return (await response.json()) as RuntimeConfig;
}

/**
 * Connexion OIDC, flux « code + PKCE » ; jetons en mémoire de session (par onglet) uniquement :
 * tablettes partagées. Un nouvel onglet se reconnecte sans mot de passe tant que la session
 * Keycloak est valide (redirection transparente) ; la reconnexion après expiration peut se faire
 * dans une fenêtre (`popup_redirect_uri`) pour garder la saisie de la page (auth.tsx).
 */
export function createUserManager(config: RuntimeConfig): UserManager {
  const origin = window.location.origin;
  return new UserManager({
    authority: `${config.identityUrl}/realms/${config.realm}`,
    client_id: config.clientId,
    redirect_uri: `${origin}/`,
    popup_redirect_uri: `${origin}/`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    scope: 'openid profile',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError | undefined,
  ) {
    super(body?.message ?? `Erreur ${String(status)}`);
  }
}

/** Évènement émis quand l'API refuse le jeton (expiré pendant une veille, par exemple). */
export const JETON_REFUSE = 'oasis:jeton-refuse';

/** Appel de l'API Oasis avec le jeton de l'utilisateur. */
export async function api<T>(user: User, path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('authorization', `Bearer ${user.access_token}`);
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  const response = await fetch(`/api/v1${path}`, { ...init, headers });
  // Jeton refusé : la session tente un renouvellement (auth.tsx), sans recharger la page.
  if (response.status === 401) window.dispatchEvent(new Event(JETON_REFUSE));
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as ApiError | undefined;
    throw new ApiRequestError(response.status, body);
  }
  // 202/204 sans corps : rien à lire.
  if (response.status === 204 || response.headers.get('content-length') === '0')
    return undefined as T;
  return (await response.json()) as T;
}
